import clsx from 'clsx'

/**
 * Tag color options.
 * - Filled: Black, Blue, Red, Neutral (white text)
 * - Tonal: Black, Blue, Red, Neutral, Green, Yellow (colored text on light background)
 */
export type TagColor = 'Black' | 'Blue' | 'Red' | 'Neutral' | 'Green' | 'Yellow'

export type TagSize = '2xs' | 'xs'

export type TagVariant = 'filled' | 'tonal'

export interface TagProps {
  /** Tag content */
  children: React.ReactNode
  /** Visual variant: filled (solid bg + white text) or tonal (light bg + border + colored text) */
  variant?: TagVariant
  /** Color. Filled supports Black, Blue, Red, Neutral. Tonal supports all. */
  color?: TagColor
  /** Size: 2xs or xs. Default is 2xs. */
  size?: TagSize
}

const filledStyles: Record<Extract<TagColor, 'Black' | 'Blue' | 'Red' | 'Neutral'>, string> = {
  Black: 'bg-brand-neutral-950 text-white',
  Blue: 'bg-brand-500 text-white',
  Red: 'bg-red-400 text-white',
  Neutral: 'bg-brand-neutral-400 text-white',
}

const tonalStyles: Record<TagColor, string> = {
  Black: 'bg-true-gray-200 ring-1 ring-inset ring-true-gray-300 text-brand-neutral-950',
  Neutral: 'bg-brand-neutral-200 ring-1 ring-inset ring-brand-neutral-300 text-brand-neutral-600',
  Blue: 'bg-brand-100 ring-1 ring-inset ring-brand-200 text-brand-800',
  Red: 'bg-red-100 ring-1 ring-inset ring-red-200 text-red-800',
  Green: 'bg-green-100 ring-1 ring-inset ring-green-200 text-green-800',
  Yellow: 'bg-yellow-100 ring-1 ring-inset ring-yellow-200', // text is size-dependent, see yellowTonalTextBySize
}

// Figma: Yellow 2xs uses text 700, Yellow xs uses text 800
const yellowTonalTextBySize: Record<TagSize, string> = {
  '2xs': 'text-yellow-700',
  xs: 'text-yellow-800',
}

const sizeStyles: Record<TagSize, string> = {
  '2xs': 'h-[14px] text-2xs',
  xs: 'h-[18px] text-xs leading-4',
}

const FILLED_COLORS: TagColor[] = ['Black', 'Blue', 'Red', 'Neutral']

/**
 * Tag component
 * Supports filled (solid background) and tonal (light background + border) variants.
 */
export function Tag({ children, variant = 'tonal', color = 'Neutral', size = '2xs' }: TagProps) {
  const resolvedColor = variant === 'filled' && !FILLED_COLORS.includes(color) ? 'Neutral' : color
  const styleClasses = variant === 'filled' ? filledStyles[resolvedColor as keyof typeof filledStyles] : tonalStyles[resolvedColor]
  const yellowText = variant === 'tonal' && resolvedColor === 'Yellow' ? yellowTonalTextBySize[size] : null

  return (
    <span
      data-ds-component="Tag"
      data-ds-variant={variant}
      data-ds-color={resolvedColor}
      data-ds-size={size}
      className={clsx(
        'inline-flex items-center justify-center px-1 pb-px rounded font-medium whitespace-nowrap flex-shrink-0 [font-variant:all-small-caps]',
        styleClasses,
        yellowText,
        sizeStyles[size]
      )}
    >
      {children}
    </span>
  )
}
